/**
 * YouTube Shorts — click, hover, or inline playback with one active video at a time.
 */

const YT_THUMB_QUALITIES = [
  'oardefault',
  'maxresdefault',
  'sddefault',
  'hqdefault',
  'mqdefault',
];

/**
 * @param {HTMLImageElement} img
 */
function bindPosterThumbnailFallback(img) {
  if (img.dataset.ytThumbBound === 'true') return;

  const videoId = img.dataset.ytVideoId;
  if (!videoId) return;

  img.dataset.ytThumbBound = 'true';

  let qualityIndex = YT_THUMB_QUALITIES.indexOf(img.dataset.ytThumbQuality || 'oardefault');
  if (qualityIndex < 0) qualityIndex = 0;

  img.addEventListener('error', () => {
    qualityIndex += 1;
    if (qualityIndex < YT_THUMB_QUALITIES.length) {
      const quality = YT_THUMB_QUALITIES[qualityIndex];
      img.dataset.ytThumbQuality = quality;
      img.src = `https://i.ytimg.com/vi/${videoId}/${quality}.jpg`;
    }
  });
}

/**
 * @param {string} url
 * @param {Record<string, string>} params
 */
function withParams(url, params) {
  const parsed = new URL(url);
  Object.entries(params).forEach(([key, value]) => {
    parsed.searchParams.set(key, value);
  });
  return parsed.toString();
}

/**
 * @param {HTMLIFrameElement} iframe
 * @param {string} func
 */
function youtubeCommand(iframe, func) {
  iframe.contentWindow?.postMessage(
    JSON.stringify({ event: 'command', func, args: '' }),
    '*'
  );
}

/**
 * @param {HTMLElement} wrap
 */
function pauseWrap(wrap) {
  const iframeEl = wrap.querySelector('iframe[data-video-type="youtube"]');
  if (!(iframeEl instanceof HTMLIFrameElement)) return;

  const baseSrc = iframeEl.dataset.embedSrc;
  if (!baseSrc) return;

  const wasActive =
    wrap.hasAttribute('data-playing') ||
    wrap.hasAttribute('data-active') ||
    iframeEl.src.includes('autoplay=1');

  if (!wasActive) return;

  youtubeCommand(iframeEl, 'stopVideo');
  youtubeCommand(iframeEl, 'pauseVideo');
  iframeEl.src = baseSrc;

  wrap.removeAttribute('data-playing');
  wrap.removeAttribute('data-active');
}

/**
 * @param {HTMLElement} activeWrap
 */
function pauseAllExcept(activeWrap) {
  const scope = activeWrap.closest('.youtube-shorts') ?? document;
  scope.querySelectorAll('.youtube-shorts__player-wrap').forEach((wrap) => {
    if (wrap !== activeWrap && wrap instanceof HTMLElement) {
      pauseWrap(wrap);
    }
  });
}

/**
 * @param {HTMLElement} wrap
 */
function bindPlayer(wrap) {
  if (wrap.dataset.youtubeShortsBound === 'true') return;
  wrap.dataset.youtubeShortsBound = 'true';

  const section = wrap.closest('.youtube-shorts');
  const interactionMode =
    wrap.dataset.interactionMode ||
    (section instanceof HTMLElement ? section.dataset.interactionMode : undefined) ||
    'hover';
  const hasHover = window.matchMedia('(hover: hover)').matches;
  const iframeEl = wrap.querySelector('iframe[data-video-type="youtube"]');
  const poster = wrap.querySelector('.youtube-shorts__poster');
  const tapShield = wrap.querySelector('.youtube-shorts__tap-shield');

  if (!(iframeEl instanceof HTMLIFrameElement)) return;

  const baseSrc = iframeEl.dataset.embedSrc || iframeEl.src;
  const isMuted = wrap.dataset.muted !== 'false';
  const playSrc =
    iframeEl.dataset.embedPlaySrc ||
    withParams(baseSrc, isMuted ? { autoplay: '1', mute: '1' } : { autoplay: '1' });

  const unmuteIfNeeded = () => {
    if (!isMuted) {
      youtubeCommand(iframeEl, 'unMute');
    }
  };

  const play = () => {
    pauseAllExcept(wrap);
    wrap.setAttribute('data-playing', '');
    wrap.setAttribute('data-active', '');

    if (!iframeEl.src.includes('autoplay=1')) {
      iframeEl.addEventListener('load', () => unmuteIfNeeded(), { once: true });
      iframeEl.src = playSrc;
    } else {
      youtubeCommand(iframeEl, 'playVideo');
      unmuteIfNeeded();
    }
  };

  const pause = () => {
    pauseWrap(wrap);
  };

  const handleActivate = (event) => {
    event.preventDefault();
    if (wrap.hasAttribute('data-playing')) {
      pause();
    } else {
      play();
    }
  };

  if (interactionMode === 'hover' && hasHover) {
    wrap.addEventListener('mouseenter', play);
    wrap.addEventListener('mouseleave', pause);
    wrap.addEventListener('focusin', play);
    wrap.addEventListener('focusout', (event) => {
      if (!wrap.contains(/** @type {Node} */ (event.relatedTarget))) {
        pause();
      }
    });
    return;
  }

  poster?.addEventListener('click', handleActivate);
  tapShield?.addEventListener('click', handleActivate);
}

/**
 * @param {ParentNode} root
 */
function syncYoutubeShortsCarouselNav(root) {
  root.querySelectorAll('.youtube-shorts .resource-list__carousel').forEach((carousel) => {
    if (!(carousel instanceof HTMLElement)) return;

    const container = carousel.closest('.youtube-shorts');
    const scroller = carousel.querySelector('slideshow-slides');
    if (!(container instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

    const arrows = carousel.querySelector('slideshow-arrows');
    const configuredPeek = container.dataset.carouselPeekSize || '0';

    const update = () => {
      const canScroll = scroller.scrollWidth - scroller.clientWidth > 1;

      if (arrows instanceof HTMLElement) {
        arrows.hidden = !canScroll;
      }

      container.style.setProperty(
        '--youtube-shorts-carousel-peek-size',
        canScroll ? `${configuredPeek}px` : '0px'
      );
    };

    if (carousel.dataset.carouselNavBound !== 'true') {
      carousel.dataset.carouselNavBound = 'true';
      new ResizeObserver(update).observe(scroller);
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }

    requestAnimationFrame(() => requestAnimationFrame(update));
  });
}

/**
 * @param {ParentNode} root
 */
function initYoutubeShorts(root) {
  root.querySelectorAll('.youtube-shorts__player-wrap').forEach((wrap) => {
    if (wrap instanceof HTMLElement) {
      bindPlayer(wrap);
    }
  });

  root.querySelectorAll('.youtube-shorts__poster-image[data-yt-video-id]').forEach((img) => {
    if (img instanceof HTMLImageElement) {
      bindPosterThumbnailFallback(img);
    }
  });

  syncYoutubeShortsCarouselNav(root);
}

function bootYoutubeShorts() {
  initYoutubeShorts(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootYoutubeShorts);
} else {
  bootYoutubeShorts();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initYoutubeShorts(event.target);
  }
});

document.addEventListener('shopify:block:select', (event) => {
  if (event.target instanceof HTMLElement) {
    initYoutubeShorts(event.target);
  }
});
